import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Bell, Settings as SettingsIcon, ArrowLeft, Smartphone, BellOff, Loader2, CheckCircle, AlertCircle, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Notification bell — polls unread-count every 60s and shows a dropdown of
 * the most recent items. The Settings gear flips the dropdown to a control
 * panel so any user (admin, manager, technician) can self-enable push and
 * tweak notification prefs without needing access to the Services page.
 *
 * Mounted exactly ONCE by Layout.js (previously mounted twice — once in the
 * mobile header, once in a floating desktop puck — which meant two
 * independent 60s polling intervals for the same user). `mobileSlot` and
 * `desktopSlot` are the actual DOM nodes of two small placeholder <div>s
 * that Layout.js renders in those exact same two visual spots (same
 * wrapper classNames/position as before). This single instance portals
 * its bell button into whichever slot is currently mounted — one fetch,
 * one 60s interval, one `unread`/`open` state shared by both — while the
 * mobile and desktop bells keep looking and behaving exactly as before.
 */
export default function NotificationBell({ onNavigate, mobileSlot, desktopSlot }) {
  const { token } = useAuth();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // "list" | "settings"
  const popRef = useRef(null);
  const btnRef = useRef(null);

  // Decide which of the two slots (mobile header vs desktop floating
  // puck) the single bell button should portal into, matching Tailwind's
  // default `md` breakpoint (768px) that already drives md:hidden /
  // hidden md:block on the two slot wrappers in Layout.js. Only one
  // portal is ever mounted at a time, so there's exactly one
  // `data-testid="notification-bell"` button in the DOM, same as before.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
  );
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchCount = async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/notifications/unread-count`, { headers });
      setUnread(r.data?.unread || 0);
    } catch (_e) {/* ignore */}
  };

  const fetchItems = async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/notifications?limit=15`, { headers });
      // Backend returns a raw array. Tolerate both shapes for safety.
      const arr = Array.isArray(r.data)
        ? r.data
        : (r.data?.items || r.data?.notifications || []);
      setItems(arr);
    } catch (_e) {/* ignore */}
  };

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Lock background scroll while the dropdown is open. Without this,
  // dragging a finger inside the panel on iOS Safari scrolls the page
  // underneath because touchmove bubbles past the floating layer (the
  // dropdown is portal'd to body, so the page is a sibling, not an
  // ancestor — pointer-events alone don't help). We freeze the body
  // and restore its previous overflow/touch-action on close.
  useEffect(() => {
    if (!open) return;
    const { body, documentElement: html } = document;
    const prev = {
      bodyOverflow: body.style.overflow,
      bodyTouchAction: body.style.touchAction,
      htmlOverflow: html.style.overflow,
    };
    body.style.overflow = "hidden";
    body.style.touchAction = "none";
    html.style.overflow = "hidden";

    // The panel itself still needs to scroll — only block touchmove that
    // originates OUTSIDE the dropdown. Marked non-passive so we can
    // preventDefault().
    const popEl = popRef.current;
    const blockOutside = (e) => {
      if (popEl && popEl.contains(e.target)) return;
      e.preventDefault();
    };
    document.addEventListener("touchmove", blockOutside, { passive: false });

    return () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.touchAction = prev.bodyTouchAction;
      html.style.overflow = prev.htmlOverflow;
      document.removeEventListener("touchmove", blockOutside);
    };
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setView("list");
      await fetchItems();
    }
  };

  const handleItemClick = async (item) => {
    if (!item.read) {
      try { await axios.post(`${API}/notifications/${item.id}/read`, null, { headers }); } catch (_e) {}
    }
    setUnread((n) => Math.max(0, n - (item.read ? 0 : 1)));
    setItems((arr) => arr.map((x) => (x.id === item.id ? { ...x, read: true } : x)));
    setOpen(false);
    if (item.link) {
      if (onNavigate) onNavigate(item.link);
      else window.location.assign(item.link);
    }
  };

  const markAllRead = async () => {
    try { await axios.post(`${API}/notifications/mark-all-read`, null, { headers }); } catch (_e) {}
    setUnread(0);
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
  };

  // "Clear all" — destructively delete every notification for this user so
  // the bell tray actually empties. Falls back to a best-effort per-item
  // delete if the bulk endpoint isn't available yet on the backend.
  const clearAll = async () => {
    const snapshot = items;
    setItems([]);
    setUnread(0);
    try {
      await axios.delete(`${API}/notifications`, { headers });
    } catch (_e) {
      // Bulk endpoint missing — fall back to per-item deletes (in parallel).
      try {
        await Promise.allSettled(
          snapshot.map((n) =>
            axios.delete(`${API}/notifications/${n.id}`, { headers }),
          ),
        );
      } catch (_e2) {/* ignore */}
    }
  };

  const dismissOne = async (id, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const target = items.find((x) => x.id === id);
    setItems((arr) => arr.filter((x) => x.id !== id));
    if (target && !target.read) setUnread((n) => Math.max(0, n - 1));
    try { await axios.delete(`${API}/notifications/${id}`, { headers }); } catch (_e) {/* ignore */}
  };

  const activeSlot = isDesktop ? desktopSlot : mobileSlot;

  const bellButton = (
    <button
      ref={btnRef}
      onClick={toggle}
      className="relative p-2 rounded-md text-slate-600 hover:bg-slate-100"
      aria-label="Notifications"
      data-testid="notification-bell"
    >
      <Bell className="w-5 h-5" />
      {unread > 0 && (
        <span
          // inline-flex + justify-center measures against content instead
          // of relying on min-width + padding, so 2-digit counts like
          // "12" can't get sub-pixel-clipped on Retina. px-1.5 gives the
          // right side of the last digit a proper gutter (px-1 was too
          // tight — the "2" bled into the padding edge). min-w-[22px]
          // still keeps 1-digit counts perfectly circular.
          className="pointer-events-none absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold tabular-nums leading-none ring-2 ring-white"
          data-testid="notification-bell-count"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </button>
  );

  return (
    <>
      {activeSlot && createPortal(bellButton, activeSlot)}

      {open && createPortal(
        <div
          ref={popRef}
          // The list used to be cut off by the mobile bottom-tab bar — the
          // dropdown's max-h ignored the safe-area inset AND the ~5rem tab
          // bar height, so the last 1-2 notifications hid behind the nav.
          // Constraining the bottom edge above the tab bar (+ safe area)
          // lets the inner list scroll all the way through.
          //
          // We portal into document.body because the mobile header has
          // `transform: translate3d(0,0,0)` (iOS scroll-pin trick), and a
          // transformed ancestor breaks `position: fixed` for descendants —
          // they get positioned relative to the ancestor instead of the
          // viewport. Portaling bypasses that entirely and the panel sits
          // where the CSS calc() expects it.
          className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-6 top-[calc(4rem+env(safe-area-inset-top)+0.25rem)] sm:top-16 sm:w-80 sm:max-w-[calc(100vw-1rem)] bottom-[calc(5rem+env(safe-area-inset-bottom)+0.5rem)] sm:bottom-auto sm:max-h-[80vh] bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden flex flex-col"
          data-testid="notification-bell-dropdown"
        >
          {view === "list" ? (
            <>
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 flex-shrink-0">
                <p className="text-sm font-semibold text-slate-700">Notifications</p>
                <div className="flex items-center gap-3 text-xs">
                  <button
                    onClick={markAllRead}
                    className="text-slate-500 hover:text-slate-700"
                    data-testid="notification-mark-all-read"
                  >
                    Mark all read
                  </button>
                  <button
                    onClick={clearAll}
                    className="text-slate-500 hover:text-red-600"
                    data-testid="notification-clear-all"
                    title="Delete all notifications"
                  >
                    Clear all
                  </button>
                  <button
                    onClick={() => setView("settings")}
                    className="text-slate-500 hover:text-slate-700 flex items-center gap-1"
                    data-testid="notification-settings-btn"
                    aria-label="Notification settings"
                  >
                    <SettingsIcon className="w-3.5 h-3.5" />
                    Settings
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto flex-1 min-h-0">
                {items.length === 0 ? (
                  <p className="text-center text-sm text-slate-400 py-8" data-testid="notification-empty">
                    You're all caught up.
                  </p>
                ) : (
                  items.map((it) => (
                    <div
                      key={it.id}
                      className={`relative w-full border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                        !it.read ? "bg-amber-50/40" : ""
                      }`}
                      data-testid={`notification-item-${it.id}`}
                    >
                      <button
                        onClick={() => handleItemClick(it)}
                        className="w-full text-left px-4 py-3 pr-10"
                      >
                        <div className="flex items-start gap-2">
                          {!it.read && <span className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-900 truncate">{it.title}</p>
                            <p className="text-xs text-slate-500 line-clamp-2">{it.body || it.message}</p>
                            <p className="text-[11px] text-slate-400 mt-0.5">
                              {it.created_at ? new Date(it.created_at).toLocaleString() : ""}
                            </p>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={(e) => dismissOne(it.id, e)}
                        className="absolute top-2 right-2 p-1 rounded text-slate-400 hover:text-red-600 hover:bg-slate-100"
                        aria-label="Dismiss notification"
                        data-testid={`notification-dismiss-${it.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <NotificationSettingsPanel
              token={token}
              onBack={() => setView("list")}
            />
          )}
        </div>,
        document.body
      )}
    </>
  );
}

/**
 * Inline settings panel inside the bell dropdown. Lets any user toggle
 * in-app + email prefs and self-enable browser/PWA push without needing
 * access to the admin Services page.
 */
function NotificationSettingsPanel({ token, onBack }) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [prefs, setPrefs] = useState({ in_app: true, email: false });
  const [savingPref, setSavingPref] = useState(null); // 'in_app' | 'email' | null

  const [pushSupported, setPushSupported] = useState(true);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushPermission, setPushPermission] = useState("default");
  const [pushBusy, setPushBusy] = useState(false);
  const [isPWA, setIsPWA] = useState(true);

  // Load prefs + push status
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/notifications/preferences`, { headers });
        if (mounted) setPrefs({
          in_app: r.data?.in_app ?? true,
          email: r.data?.email ?? false,
        });
      } catch (_e) {/* ignore */}

      const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
      if (mounted) setPushSupported(supported);
      if (mounted) setPushPermission(("Notification" in window) ? Notification.permission : "default");

      const standalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
      if (mounted) setIsPWA(standalone);

      if (supported) {
        try {
          const r = await axios.get(`${API}/push/status`, { headers });
          if (mounted) setPushSubscribed(!!r.data?.enabled);
        } catch (_e) {/* ignore */}
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSavingPref(key);
    try {
      await axios.put(`${API}/notifications/preferences`, next, { headers });
      toast.success("Saved");
    } catch (_e) {
      setPrefs(prefs); // revert
      toast.error("Failed to save");
    } finally {
      setSavingPref(null);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
  };

  const enablePush = async () => {
    setPushBusy(true);
    try {
      if (!isPWA) {
        toast.error("Open from your Home Screen first, then try again.");
        setPushBusy(false);
        return;
      }
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== "granted") {
        toast.error(permission === "denied"
          ? "Notifications blocked. Enable in iPhone Settings → FieldReady."
          : "Permission not granted");
        setPushBusy(false);
        return;
      }
      const keyResp = await axios.get(`${API}/push/vapid-public-key`);
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyResp.data.publicKey),
      });
      await axios.post(`${API}/push/subscribe`, { subscription: subscription.toJSON() }, { headers });
      setPushSubscribed(true);
      toast.success("Push notifications enabled.");
    } catch (e) {
      toast.error("Failed to enable: " + (e.message || "unknown error"));
    } finally {
      setPushBusy(false);
    }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await sub.unsubscribe();
        await axios.delete(`${API}/push/unsubscribe`, { data: { subscription: sub.toJSON() }, headers });
      }
      setPushSubscribed(false);
      toast.success("Push notifications disabled.");
    } catch (_e) {
      toast.error("Failed to disable");
    } finally {
      setPushBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      await axios.post(`${API}/push/test`, null, { headers });
      toast.success("Test sent — check your phone.");
    } catch (_e) { toast.error("Failed to send test"); }
  };

  return (
    <div className="flex flex-col" data-testid="notification-settings-panel">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          data-testid="notification-settings-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <p className="text-sm font-semibold text-slate-700">Notification Settings</p>
        <span className="w-10" />
      </div>

      <div className="overflow-y-auto px-4 py-3 space-y-4">
        {/* Push Notifications — primary CTA, accessible to everyone */}
        <div className={`rounded-lg p-3 border-2 ${pushSubscribed ? "bg-green-50 border-green-200" : "bg-green-50 border-green-200"}`}>
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-green-700" />
            <p className="font-semibold text-sm text-green-900">Push Notifications</p>
            <span className="text-[10px] bg-green-200 text-green-800 px-1.5 py-0.5 rounded-full font-bold">FREE</span>
          </div>
          <p className="text-xs text-green-800 mb-3">
            Instant alerts on your phone — even when the app is closed.
          </p>

          {!pushSupported ? (
            <p className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded p-2">
              Not supported in this browser. Use Safari/Chrome from your Home Screen.
            </p>
          ) : pushPermission === "denied" ? (
            <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2 flex gap-1">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Notifications blocked. Open iPhone <strong>Settings → FieldReady → Notifications</strong> to allow.</span>
            </p>
          ) : !isPWA ? (
            <p className="text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded p-2 flex gap-1">
              <Smartphone className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Tap <strong>Share → Add to Home Screen</strong>, then open from your home screen and try here again.</span>
            </p>
          ) : pushSubscribed ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-green-900">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Enabled on this device.
              </div>
              <div className="flex gap-2">
                <button
                  onClick={sendTest}
                  className="flex-1 text-xs px-3 py-2 rounded border border-green-300 bg-white text-green-800 hover:bg-green-100 font-medium"
                  data-testid="push-test-btn"
                >
                  Send test
                </button>
                <button
                  onClick={disablePush}
                  disabled={pushBusy}
                  className="flex-1 text-xs px-3 py-2 rounded border border-red-300 bg-white text-red-700 hover:bg-red-50 font-medium flex items-center justify-center gap-1"
                  data-testid="push-disable-btn"
                >
                  {pushBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellOff className="w-3 h-3" />}
                  Disable
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={enablePush}
              disabled={pushBusy}
              className="w-full text-sm px-3 py-2 rounded bg-green-600 hover:bg-green-700 text-white font-bold flex items-center justify-center gap-2"
              data-testid="push-enable-btn"
            >
              {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
              Enable Push Notifications
            </button>
          )}
        </div>

        {/* In-app + Email toggles */}
        <div className="space-y-2">
          <ToggleRow
            label="In-app notifications"
            description="Show alerts on this dashboard bell."
            checked={prefs.in_app}
            saving={savingPref === "in_app"}
            onChange={() => togglePref("in_app")}
            testId="notif-toggle-in-app"
          />
          <ToggleRow
            label="Email notifications"
            description="Also send a copy to your email."
            checked={prefs.email}
            saving={savingPref === "email"}
            onChange={() => togglePref("email")}
            testId="notif-toggle-email"
          />
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, description, checked, saving, onChange, testId }) {
  return (
    <button
      onClick={onChange}
      disabled={saving}
      className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition text-left"
      data-testid={testId}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
      <span
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition ${checked ? "bg-amber-500" : "bg-slate-300"}`}
        aria-hidden="true"
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition ${checked ? "translate-x-5" : "translate-x-0.5"} translate-y-0.5`}
        />
      </span>
    </button>
  );
}
